/**
 * Landing Page — the two product territories.
 *
 * This is an entry screen behind the login, not a marketing page: its one job
 * is to let an actuary pick a book and get in, without scrolling. The whole
 * composition is therefore sized to the viewport (100dvh minus the 72px navbar
 * and its hairline), and every vertical measure is clamped against `vh` so it
 * holds from a 600px-tall laptop up to a 4K panel.
 *
 * Colour, type and radius all come from the design system in index.css
 * (Deep Emerald primary, Actuarial Blue accent, Harvest Clay secondary,
 * warm-paper ground; Manrope headings, IBM Plex body, JetBrains mono labels).
 * Every value is read through `hsl(var(--token))` rather than written as a
 * literal, so the page follows the theme into dark mode instead of stranding
 * one theme's text on the other theme's ground.
 *
 * The two halves are told apart by hue and by what they MEASURE — RWBCIS reads
 * a weather station, PMFBY reads a crop-cutting experiment — which is the real
 * difference between the products and the thing the "Reads" strip states.
 */

import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Whether the band's clip freezes for a viewer whose OS asks for less motion.
 *
 * Left OFF deliberately. Windows Server, most RDP sessions and a lot of
 * locked-down corporate Windows images report `prefers-reduced-motion: reduce`
 * as a DEFAULT rather than as anything the person chose, so honouring it here
 * silently froze the band for a large share of exactly this tool's audience.
 * The clip is ambient decoration — slow, muted, behind a scrim, no parallax and
 * no flashing — so the vestibular risk the preference exists for is low, and
 * the GIF it replaced ignored the preference outright.
 *
 * Flip to `true` to respect the setting again; everything else is already wired.
 */
const RESPECT_REDUCED_MOTION = false;

/** Height of the fixed navbar (72px) plus its hairline — see NavBar.css. */
const SHELL = "calc(100dvh - 73px)";

const modules = [
  {
    code: "RWBCIS",
    key: "rwbcis",
    title: "Weather-Based Crop Insurance",
    tone: "accent",
    // The band is a muted looping clip, encoded from sunrise-timelapse.gif.
    poster: "/assets/sunrise.jpg",
    video: { webm: "/assets/sunrise.webm", mp4: "/assets/sunrise.mp4" },
    href: "/RWBCIS",
    points: [
      ["Term sheets read by AI", "every essential parameter, is captured."],
      ["Multi Weather Data Sources", "Flexibility to use own or popular data sets on fly."],
      ["Trace each number", "All payout numbers can be tracked."],
    ],
  },
  {
    code: "PMFBY",
    key: "pmfby",
    title: "Pradhan Mantri Fasal Bima Yojana",
    tone: "primary",
    poster: "/assets/crop.jpg",
    video: { webm: "/assets/crop.webm", mp4: "/assets/crop.mp4" },
    href: "/PMFBY",
    points: [
      ["Data Prep by AI", "Data from multi files into one standard structure."],
      ["Multiple Pricing Options", "Different variations in pricing can be achieved with ease."],
      ["Beyond Yield Loss", "cup & cap, add-on loadings and additional covers."],
    ],
  },
  // kept in code for future product lines; not rendered on the home page
  { code: "PROP", title: "Property Related Pricing & Analytics", hidden: true },
  { code: "IOT", title: "IoT Based Covers Pricing & Analytics", hidden: true },
  { code: "NATCAT", title: "NAT CAT Pricing & Analytics", hidden: true },
  { code: "BI", title: "Business Interruption Pricing & Analytics", hidden: true },
  { code: "WX", title: "Weather Risk Pricing & Analytics", hidden: true },
];

const TONE = {
  primary: {
    text: "text-primary",
    ring: "focus-visible:ring-primary/40",
    hoverBorder: "group-hover:border-primary/30",
    // a drawn field motif, so a missing photo still reads as designed
    fallback:
      "linear-gradient(150deg,hsl(var(--primary)) 0%,hsl(var(--primary)/.82) 55%,hsl(163 72% 30%) 100%)",
    marker: "hsl(var(--primary))",
    wash: "hsl(var(--primary) / 0.09)",
  },
  accent: {
    text: "text-accent",
    ring: "focus-visible:ring-accent/40",
    hoverBorder: "group-hover:border-accent/30",
    fallback:
      "linear-gradient(150deg,hsl(217 70% 32%) 0%,hsl(var(--accent)) 58%,hsl(217 91% 72%) 100%)",
    marker: "hsl(var(--accent))",
    wash: "hsl(var(--accent) / 0.09)",
  },
};

const MINUTE = 60e3, HOUR = 60 * MINUTE, DAY = 24 * HOUR;

/** "just now" / "12m ago" / "3h ago" / "2d ago" / "1w ago" / "4mo ago". */
function ago(iso) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const d = Date.now() - t;
  if (d < 0 || d < MINUTE) return "just now";
  // "always", not "auto": auto renders "yesterday" / "last wk." / "last mo.",
  // which reads vaguely beside the exact "3d ago" the shorter buckets produce.
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "always", style: "narrow" });
  const [amount, unit] =
    d < HOUR ? [d / MINUTE, "minute"] :
    d < DAY ? [d / HOUR, "hour"] :
    d < 7 * DAY ? [d / DAY, "day"] :
    d < 30 * DAY ? [d / (7 * DAY), "week"] :
    d < 365 * DAY ? [d / (30 * DAY), "month"] : [d / (365 * DAY), "year"];
  return rtf.format(-Math.max(1, Math.round(amount)), unit);
}

/**
 * Per-product-line activity for the band chips. Silent on failure: this is a
 * decorative timestamp and must never keep the landing page from rendering —
 * an older backend without /workspaces/summary simply shows no chip.
 */
function useWorkspaceSummary() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    axios.get(`${API}/workspaces/summary`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return data;
}

/**
 * True when the viewer has asked for less motion. Starts false so the first
 * render matches the server's, then corrects on mount — a looping clip is
 * exactly the kind of thing this preference exists for, and a GIF gave us no
 * way to honour it.
 */
function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (!RESPECT_REDUCED_MOTION) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

/**
 * The band's moving image. The poster paints immediately (~80 KB) so the card
 * is never empty while ~0.4 MB of video arrives behind it; WebM is offered
 * first and MP4 carries Safari. If the clip cannot play — codec, policy,
 * missing file — the poster simply stays, which is why it is also the
 * `<video>`'s background rather than a separate element to clean up.
 */
const BandMedia = ({ m }) => {
  const reduced = usePrefersReducedMotion();
  const ref = React.useRef(null);

  // Muted autoplay is permitted everywhere, but React does not always put
  // `muted` on the node before the browser decides, and a paused <video> just
  // shows its poster — which looks identical to a broken file. So set muted on
  // the element and ask for playback explicitly; if the policy still refuses,
  // the poster remains and nothing is broken.
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.muted = true;
    el.defaultMuted = true;
    const start = () => { const r = el.play(); if (r && r.catch) r.catch(() => {}); };
    start();
    el.addEventListener("canplay", start, { once: true });
    return () => el.removeEventListener("canplay", start);
  }, [m.code]);

  // one line: a class string broken across lines reads to Tailwind's scanner as
  // prose and it warns about the arbitrary values
  const common = "absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.035] motion-reduce:transition-none motion-reduce:group-hover:scale-100";

  if (!m.video || reduced) {
    return (
      <img src={m.poster} alt="" decoding="async" fetchpriority="high"
        onError={(e) => e.currentTarget.remove()} className={common} />
    );
  }
  return (
    <video
      // React can drop `muted` from the DOM node, and an unmuted clip is
      // blocked from autoplaying — so set it on the element itself too.
      ref={ref}
      className={common}
      poster={m.poster}
      autoPlay muted loop playsInline preload="auto" aria-hidden="true"
      style={{ backgroundImage: `url(${m.poster})`, backgroundSize: "cover",
               backgroundPosition: "center" }}
    >
      <source src={m.video.webm} type="video/webm" />
      <source src={m.video.mp4} type="video/mp4" />
    </video>
  );
};

const Arrow = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    className="transition-transform duration-300 ease-out group-hover:translate-x-1
      motion-reduce:transition-none motion-reduce:group-hover:translate-x-0">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

/** Shared treatment for anything sitting ON the band: the same translucent
 *  slab, hairline and blur as the scheme-code chip, so the two read as a pair
 *  rather than as one chip plus an afterthought. */
const ON_IMAGE =
  "font-mono absolute bottom-3 z-[1] rounded-full border border-white/25 bg-black/40 " +
  "px-2.5 py-[5px] text-[0.64rem] font-medium text-white backdrop-blur-[6px]";

/** One territory: photographic band, identity, what it does, the way in. */
const Territory = ({ module: m, stat }) => {
  const t = TONE[m.tone];
  const titleId = `${m.code.toLowerCase()}-title`;

  return (
    <Link
      to={m.href}
      aria-labelledby={titleId}
      className={`group flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border
        bg-card text-left no-underline outline-none
        shadow-[0_1px_2px_hsl(240_10%_4%/.04),0_14px_34px_-14px_hsl(240_10%_4%/.13)]
        transition-[transform,box-shadow] duration-300 ease-out
        hover:-translate-y-[3px] hover:shadow-[0_2px_4px_hsl(240_10%_4%/.05),0_26px_52px_-18px_hsl(240_10%_4%/.22)]
        focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background
        motion-reduce:transition-none motion-reduce:hover:translate-y-0
        ${t.ring} ${t.hoverBorder}`}
    >
      {/* Photographic band. Height is viewport-driven so the panel never forces a scroll. */}
      <div
        className="relative h-[clamp(108px,23.5vh,260px)] shrink-0 overflow-hidden"
        style={{
          backgroundImage:
            `repeating-linear-gradient(100deg,hsl(0 0% 100%/.15) 0 2px,transparent 2px 15px),${t.fallback}`,
        }}
      >
        <BandMedia m={m} />
        <div aria-hidden="true" className="absolute inset-0
          bg-[linear-gradient(180deg,hsl(240_10%_4%/0)_40%,hsl(240_10%_4%/.5)_100%)]" />
        <span className={`${ON_IMAGE} left-4 tracking-[0.18em]`}>{m.code}</span>

        {/* Activity, on the band and in the band's own visual register. The
            wording follows what the server actually knows: "opened" only when
            a real open was recorded, otherwise "updated" — a project can be
            changed by a background job nobody opened. */}
        {stat?.since && ago(stat.since) && (
          <span className={`${ON_IMAGE} right-4 tracking-[0.04em]`}>
            {stat.basis === "opened" ? "Last opened" : "Last updated"} {ago(stat.since)}
          </span>
        )}
        {stat && !stat.since && (
          // No timestamp at all: either a fresh workspace, or documents that
          // predate `updated_date`. Say which rather than rendering nothing —
          // a missing chip is indistinguishable from a failed fetch.
          <span className={`${ON_IMAGE} right-4 tracking-[0.04em]`}>
            {stat.projects === 0
              ? "No projects yet"
              : `${stat.projects} project${stat.projects === 1 ? "" : "s"}`}
          </span>
        )}
      </div>

      {/* Body. `justify-between` puts both CTAs on one baseline whatever the panel height. */}
      <div className="flex min-h-0 flex-1 flex-col gap-[clamp(8px,1.65vh,17px)]
        px-[clamp(16px,1.6vw,24px)] py-[clamp(11px,2vh,22px)]">

        <h2 id={titleId} className="font-heading m-0 text-[clamp(1.02rem,1.45vw,1.28rem)]
          font-extrabold leading-[1.18] tracking-[-0.022em] text-foreground text-balance">
          {m.title}
        </h2>

        <ul className="m-0 flex list-none flex-col gap-[clamp(7px,1.3vh,13px)] p-0">
          {m.points.map(([lead, rest]) => (
            <li key={lead} className="flex gap-2.5 text-[clamp(0.82rem,0.96vw,0.9rem)]
              leading-[1.5] text-pretty">
              {/* a filled lozenge, not a tick and not a number: these are capabilities —
                  nothing is completed and nothing is in sequence */}
              <span aria-hidden="true" className="mt-[0.52em] h-[7px] w-[7px] shrink-0 rounded-[2px]"
                style={{ background: t.marker }} />
              <span className="text-muted-foreground">
                <b className="font-semibold text-foreground">{lead}</b>
                {" — "}
                {rest}
              </span>
            </li>
          ))}
        </ul>

        <span className={`font-heading mt-auto inline-flex items-center gap-[7px] pt-1
          text-[0.845rem] font-bold ${t.text}`}>
          Open {m.code}
          <Arrow />
        </span>
      </div>
    </Link>
  );
};

const LandingPage = () => {
  const live = modules.filter((m) => !m.hidden);
  const summary = useWorkspaceSummary();

  return (
    // overflow-auto rather than hidden: at every realistic size the content fits
    // and no scrollbar appears, but on a very short window it scrolls instead of
    // clipping — a clipped line would be a bug, a scrollbar is only a fallback.
    <div className="relative overflow-auto bg-background" style={{ height: SHELL }}>
      {/* The two territories. The ground itself carries a whisper of each product's
          hue and they dissolve through the middle, so the page reads as divided
          without a rule being drawn. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: [
            `radial-gradient(115% 80% at 4% 88%, ${TONE.accent.wash} 0%, transparent 56%)`,
            `radial-gradient(115% 80% at 96% 88%, ${TONE.primary.wash} 0%, transparent 56%)`,
          ].join(","),
        }}
      />

      <div className="relative mx-auto flex min-h-full w-full max-w-[1200px] flex-col justify-center
        gap-[clamp(14px,2.9vh,38px)] px-5 py-[clamp(12px,2.5vh,36px)] sm:px-8 lg:px-12">

        {/* Masthead. Centred, and the headline holds ONE line from 768px up
            (`md:whitespace-nowrap`); below that it is allowed to wrap rather
            than shrink to an unreadable size. The two-hue gradient is not
            decoration here — emerald and blue are the RWBCIS and PMFBY accents,
            so the headline literally spans the two territories below it. */}
        <header className="flex flex-col items-center gap-[clamp(5px,1.1vh,13px)] text-center">
          <span className="font-mono inline-flex items-center gap-[0.6em] text-[0.66rem]
            font-medium uppercase tracking-[0.2em] text-secondary">
            <span aria-hidden="true" className="h-px w-[24px]
              bg-[linear-gradient(90deg,transparent,hsl(var(--secondary)/.5))]" />
            Pycrop · Crop Insurance Workspace
            <span aria-hidden="true" className="h-px w-[24px]
              bg-[linear-gradient(270deg,transparent,hsl(var(--secondary)/.5))]" />
          </span>

          <h1 className="font-heading m-0 text-[clamp(1.6rem,4vw,3rem)] font-extrabold
            leading-[1.08] tracking-[-0.033em] text-foreground text-balance md:whitespace-nowrap">
            AI-Powered{" "}
            <span className="bg-gradient-to-r from-accent via-accent to-primary bg-clip-text
              text-transparent">
              Pricing &amp; Analytics
            </span>
          </h1>

          <p className="m-0 max-w-[62ch] text-[clamp(0.88rem,1.05vw,1.02rem)] leading-[1.6]
            text-muted-foreground text-balance">
            Actuarial pricing and risk analytics for crop insurance — RWBCIS &amp; PMFBY
          </p>
        </header>

        {/* RWBCIS left, PMFBY right. Equal tracks: neither book is the default. */}
        {/* The gutter is ~2.2x each card's inner padding, so the space BETWEEN the
            two reads as larger than the space within either and they separate
            into two choices; it also lets the ground wash show through the
            middle, which is what divides the page in place of a rule. */}
        <div className="grid min-h-0 grid-cols-1 gap-[clamp(18px,3.6vw,52px)]
          md:grid-cols-2 md:items-stretch">
          {live.map((m) => (
            <Territory key={m.code} module={m} stat={summary?.[m.key]} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
